import { API_BASE_URL } from '../../utils/apiConfig';
import type { TargetMap } from './plan/planShared';

/* Thin fetch wrapper for /app member endpoints. Returns the parsed body;
   throws MemberApiError with the server's code/message on !ok. */

export class MemberApiError extends Error {
  status: number;
  code?: string;
  body: any;
  constructor(status: number, body: any) {
    super(body?.error || `Request failed (${status})`);
    this.status = status;
    this.code = body?.code;
    this.body = body;
  }
}

export async function mfetch<T = any>(
  token: string,
  path: string,
  opts: { method?: string; body?: any; params?: Record<string, string | undefined> } = {}
): Promise<T> {
  const url = new URL(`${API_BASE_URL}/app${path}`, window.location.origin);
  for (const [k, v] of Object.entries(opts.params ?? {})) {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v);
  }
  const res = await fetch(url.toString(), {
    method: opts.method ?? 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(opts.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
  });
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    /* non-JSON body */
  }
  if (!res.ok) throw new MemberApiError(res.status, body);
  return body as T;
}

// ── Shared member API shapes (mirror packages/api responses) ────────────────

export interface Slot {
  id: string;
  bookableType: 'class' | 'spa' | 'pt' | 'consult' | 'facility';
  name: string;
  category: string | null;
  date: string;
  startTime: string;
  endTime: string;
  durationMinutes: number;
  coach: { id: string; name: string } | null;
  roomName: string | null;
  branchName: string | null;
  capacity: number;
  bookedCount: number;
  spotsLeft: number;
  waitlistCount: number;
  waitlistEnabled: boolean;
  entitlement: 'included' | 'paid' | 'pack';
  price: number | null;
  packRemaining: number | null;
  /** The gym lets a member book this unpaid (pay at the desk). Absent reads as true. */
  allowUnpaidBooking?: boolean;
  cancellationCutoffHours: number;
  /** Back-to-back slots one booking may take (the gym's limit). Absent on an older backend: 3. */
  maxSeatsPerBooking?: number;
  myBookingId: string | null;
  onWaitlist: boolean;
}

export interface SlotGroup {
  period: 'morning' | 'afternoon' | 'evening';
  availableCount: number;
  slots: Slot[];
}

export interface MemberBooking {
  id: string;
  status: string; // booked | waitlisted | attended | no_show | cancelled
  outcome: string;
  paymentType: string | null;
  entitlement: 'included' | 'pack' | 'paid' | null;
  position?: number;
  bookedAt: string;
  checkedInAt: string | null;
  autoMarked: boolean;
  /** Court-grid bookings span multiple cells; absent on waitlist rows. */
  units?: number;
  bookingGroupId?: string | null;
  /** Materialised from the member's weekly standing slot. */
  standingBookingId?: string | null;
  /**
   * An online booking nobody has paid for yet: what it owes and when the seat
   * is released. A multi-seat booking carries its whole total and earliest
   * deadline on every seat. Null everywhere else.
   */
  pay?: { amount: number; expiresAt: string } | null;
  /**
   * A drop-in to be paid at the gym that nobody has paid for yet: what the
   * booking still owes (a multi-seat booking's whole total, on every seat).
   * Null once the desk has recorded the money, for bookings on a plan or pack,
   * and where the app collects instead (`pay`).
   */
  amountDue?: number | null;
  /**
   * Who may cancel it: the member while nothing is paid on it (a plan, a pack,
   * free, or not paid yet); the gym once money is taken — the gym cancels and
   * arranges any refund by hand. Absent on an older backend (bookingPay.cancelPolicy).
   */
  cancelBy?: 'member' | 'gym';
  /** How many spots the booking holds — all of them go together when it is cancelled. */
  spots?: number;
  session: {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
    status: string;
    class: { id: string; name: string; bookableType: string; category: string | null; cancellationCutoffHours: number };
    branch: { id?: string; displayName: string } | null;
    room: { id?: string; name: string } | null;
    /** Assigned staff (therapist/trainer); absent on waitlist rows. */
    coach?: { id: string; name: string | null } | null;
  };
}

// ── On-demand bookables (appointments / court grid) ─────────────────────────

export interface OnDemandService {
  classId: string;
  name: string;
  bookableType: 'class' | 'spa' | 'pt' | 'consult' | 'facility';
  category: string | null;
  scheduleMode: 'appointments' | 'grid';
  durationMinutes: number;
  price: number | null; // GST-inclusive
  cancellationCutoffHours: number;
  minDurationCells: number | null;
  maxDurationCells: number | null;
  /** Courts one booking may take (grid); 1 for appointments. */
  maxSeatsPerBooking?: number;
}

export interface AppointmentTimesData {
  date: string;
  timezone: string;
  durationMinutes: number;
  intervalMinutes: number;
  times: Array<{ startTime: string; staffIds: string[] }>;
  staff: Array<{ id: string; name: string; role: string }>;
  rooms: Array<{ id: string; name: string }>;
  branchId: string;
  assignmentPolicy: 'member' | 'auto' | 'manual';
  entitlement: 'included' | 'pack' | 'paid';
  price: number | null;
  packRemaining: number | null;
  /** The gym lets a member book this unpaid (pay at the desk). Absent reads as true. */
  allowUnpaidBooking?: boolean;
  cancellationCutoffHours: number;
  /** Always 1: an appointment is one seat. */
  maxSeatsPerBooking?: number;
  /** Set when the member already asked to be notified for this day. */
  waitlistEntryId: string | null;
}

/** One line of POST /member/bookings { slotIds } — consecutive-slot checkout. */
export interface SlotGroupBookingLine {
  id: string;
  status: string;
  paymentType: string | null;
  entitlement: 'included' | 'pack' | 'paid';
  startTime: string;
  endTime: string;
  purchaseId: string | null;
}

export interface CourtGridData {
  date: string;
  timezone: string;
  cellMinutes: number;
  dayStart: string;
  dayEnd: string;
  courts: Array<{ id: string; name: string; cells: Array<{ startTime: string; available: boolean }> }>;
  minDurationCells: number;
  maxDurationCells: number | null;
  pricePerCell: number | null;
  branchId: string;
  entitlement: 'included' | 'pack' | 'paid';
  packRemaining: number | null;
  /** The gym lets a member book this unpaid (pay at the desk). Absent reads as true. */
  allowUnpaidBooking?: boolean;
  cancellationCutoffHours: number;
  /** Courts one booking may take. Absent on an older backend: 4. */
  maxSeatsPerBooking?: number;
}

/**
 * How many spots one booking may take: back-to-back slots, or courts. The gym
 * sets it per bookable and the server refuses more (SEAT_LIMIT); an older
 * backend does not send it, and implied what this page used to hardcode.
 * Same rule as the mobile app's `seatLimit()` (lib/coverage.ts).
 */
export function seatLimit(bookable: { maxSeatsPerBooking?: number } | null | undefined, shape: 'slots' | 'courts'): number {
  const n = bookable?.maxSeatsPerBooking;
  return typeof n === 'number' && n >= 1 ? n : shape === 'courts' ? 4 : 3;
}

export interface GridSelection {
  roomId: string;
  startTime: string; // first cell HH:MM
  cells: number; // contiguous cell count
}

export interface GridBookingLine {
  id: string;
  status: string;
  paymentType: string | null;
  entitlement: 'included' | 'pack' | 'paid';
  units: number;
  roomName: string;
  startTime: string;
  endTime: string;
  purchaseId: string | null;
}

export interface RescheduleResult {
  bookingId: string;
  session: {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
    roomName: string | null;
    className: string;
  };
}

/** One booking drawn from a pack. Cancelled bookings gave their credit back and are not listed. */
export interface PackUsageEntry {
  bookingId: string;
  name: string;
  date: string;
  startTime: string;
  /** Credits it took: court-hours for a court, 1 otherwise. */
  units: number;
  status: 'booked' | 'attended' | 'no_show';
}

/** A pack (or granted credits) and what it has been used for — GET /member/packs. Mirrors gympe-app types/api.ts. */
export interface MemberPack {
  id: string;
  name: string;
  purchaseId: string | null;
  invoiceId: string | null;
  /** Sessions in the pack — per `period` when it refills weekly/monthly. */
  total: number;
  used: number;
  remaining: number;
  period: string | null;
  validFrom: string | null;
  validTo: string | null;
  state: 'active' | 'used_up' | 'expired';
  /** Sessions counted as used before bookings carried the usage. */
  carriedOver: number;
  usage: PackUsageEntry[];
}

export interface ExpiringCredit {
  id: string;
  remainingCredits: number;
  expiresAt: string;
  classId: string | null;
}

// ── Workout Plan & Calendar (personal — no gymId) ───────────────────────────

export interface PlanItemRow {
  id: string;
  exerciseName: string;
  workoutType: string;
  position: number;
  target: TargetMap;
  actual: TargetMap | null;
  status: 'planned' | 'done' | 'skipped' | 'rolled';
  source: 'planned' | 'added_mid_session' | 'trainer' | 'booking';
  rolledFromDate: string | null;
}

export interface PlanDayRow {
  id: string;
  date: string; // YYYY-MM-DD, fixed — reordering moves focuses, never dates
  focus: string;
  isRest: boolean;
  position: number;
  startTime: string | null;
  endTime: string | null;
  items: PlanItemRow[];
}

export interface WorkoutPlan {
  id: string;
  recipeKey: string;
  startsOn: string;
  horizonDays: number;
  status: 'active' | 'draft' | 'archived';
  createdBy: string; // 'member' | trainer user id
  allowMemberEdits: boolean;
  days: PlanDayRow[];
}

export interface PlanRuleRow {
  id: string;
  triggerKey: string;
  actionKey: string;
  triggerParams?: Record<string, unknown> | null;
  actionParams?: Record<string, unknown> | null;
  enabled: boolean;
  lastFiredAt: string | null;
}

/** GET /app/member/gyms/:gymId/plans — membership-plan browsing (A3). */
export interface GymMembershipPlan {
  id: string;
  name: string;
  description: string | null;
  price: number | null;
  gstPercent: number | null;
  durationMonths: number | null;
  features: Record<string, unknown> | null;
}

export const BOOKABLE_TYPE_LABELS: Record<string, string> = {
  class: 'Classes',
  spa: 'Spa & Recovery',
  pt: 'Personal Training',
  consult: 'Consults',
  facility: 'Facility',
};

// ── Invoices (GET /member/invoices/:purchaseId) ─────────────────────────────
// One document per sale, built by the server from the stored sale; its PDF is
// drawn from the same document. Mirrors gympe-app types/api.ts InvoiceDocument.

export interface InvoiceLine {
  description: string;
  detail: string | null;
  booking: { bookingId: string | null; date: string; startTime: string; endTime: string; roomName: string | null; staffName: string | null; status: string | null } | null;
  hsnSac: string | null;
  quantity: number;
  rate: number;
  discount: number;
  taxableValue: number;
  gstPercent: number;
  cgst: number;
  sgst: number;
  igst: number;
  amount: number;
}

export interface InvoiceDocument {
  id: string;
  number: string | null;
  date: string;
  documentType: 'tax_invoice' | 'bill_of_supply';
  title: 'Tax Invoice' | 'Bill of Supply';
  status: 'paid' | 'partial' | 'due' | 'cancelled';
  placeOfSupply: { code: string; name: string | null } | null;
  seller: { legalName: string; tradeName: string; gstin: string | null; address: string | null; branchAddress: string | null; phone: string | null; email: string | null; logoUrl: string | null };
  buyer: { name: string; phone: string | null; address: string | null; gstin: string | null; legalName: string | null };
  lines: InvoiceLine[];
  totals: { subtotal: number; discount: number; taxableValue: number; cgst: number; sgst: number; igst: number; tax: number; total: number; amountInWords: string };
  payments: Array<{ date: string; method: string; reference: string | null; amount: number }>;
  paid: number;
  balanceDue: number;
  dueDate: string | null;
  notes: string | null;
}

/** The invoice PDF the server renders from the same document, saved as a download. */
export async function downloadInvoicePdf(token: string, inv: Pick<InvoiceDocument, 'id' | 'number'>): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/app/member/invoices/${inv.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new MemberApiError(res.status, await res.json().catch(() => null));
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = `Invoice-${inv.number ?? inv.id.slice(0, 8).toUpperCase()}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
